import ScrollModal from "@/components/common/scroll-modal";
import {
  Heading,
  Button,
  Icon,
  useToast,
  Avatar,
  AvatarFallback,
} from "@elaniintc/goes-design-system";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import type { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  emailTemplateSchema,
  type EmailTemplateFormData,
} from "@/schemas/email-templates";
import { useEffect, useState } from "react";
import {
  useUpdateEmailTemplate,
  useDeleteEmailTemplate,
} from "@/queries/templates/email-templates.api";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import DeactivateEmailTemplateModal from "./DeactivateEmailTemplateModal";

interface EditEmailTemplateModalProps {
  templateData: EmailTemplateFormData;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onDeactivate?: () => void;
  canDelete?: boolean;
}

export default function EditEmailTemplateModal({
  templateData,
  open,
  onClose,
  onSuccess,
  onDeactivate,
  canDelete = false,
}: EditEmailTemplateModalProps) {
  const { hasPermission } = useAuthorization();
  const canDeactivate = hasPermission(Permissions["email-templates"].delete);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setError,
    clearErrors,
    reset,
  } = useForm({
    resolver: zodResolver(emailTemplateSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: templateData,
  });
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const { showToast } = useToast();
  const { mutateAsync: updateTemplate, isPending: isLoadingUpdate } =
    useUpdateEmailTemplate(templateData?.id || "");
  const { mutateAsync: deleteTemplate, isPending: isLoadingDelete } =
    useDeleteEmailTemplate();

  useEffect(() => {
    reset({
      id: templateData?.id || "",
      name: templateData?.name || "",
      description: templateData?.description || "",
    });
  }, [open, templateData, reset]);

  const onSubmit = async (data: EmailTemplateFormData) => {
    try {
      await updateTemplate({
        name: data.name,
        description: data.description || undefined,
      });

      showToast({
        title: "Cambios guardados",
        description:
          "Los cambios realizados en la plantilla se guardaron correctamente.",
        options: {
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square">
                <Icon type="far" name="save" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
      clearErrors();
      handleClose();
      onSuccess();
    } catch (e) {
      const error = e as AxiosError<{
        message?: string;
        errors?: { [key: string]: string[] };
      }>;
      setError("name", {
        type: "server",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Error al actualizar la plantilla",
      });
      showToast({
        title: "Error al guardar",
        description:
          "Los cambios no pudieron ser guardados. Por favor vuelve a intentarlo.",
        options: {
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="danger" shape="square">
                <Icon type="far" name="circle-exclamation" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    }
  };

  const handleDeactivate = async () => {
    try {
      await deleteTemplate(templateData.id as string);
      showToast({
        title: "Plantilla desactivada",
        description:
          "La plantilla ha sido desactivada correctamente. Puedes volver a activarla desde su configuración.",
        options: {
          duration: 5000,
          type: "error",
        },
      });
      setShowDeactivateDialog(false);
      onDeactivate?.();
      handleClose();
    } catch (e) {
      console.error("Error al desactivar la plantilla:", e);
      showToast({
        title: "Error al desactivar la plantilla",
        description:
          "Ha ocurrido un error al intentar desactivar la plantilla. Por favor, inténtalo de nuevo.",
        options: {
          duration: 5000,
          type: "error",
        },
      });
    }
  };

  const handleClose = () => {
    reset({
      name: "",
      description: "",
    });
    onClose();
  };

  return (
    <>
      <ScrollModal
        isOpen={open}
        onClose={handleClose}
        modalContentClassName="w-full max-w-[620px] max-h-[90vh] flex flex-col"
        header={
          <div className="flex items-center justify-between px-8 py-3">
            <DialogTitle className="sr-only">Editar Plantilla</DialogTitle>
            <DialogDescription className="sr-only">
              Formulario para editar una plantilla de email
            </DialogDescription>
            <Heading variant="h5" className="!mb-0">
              Editar Plantilla
            </Heading>
            <Button
              type="button"
              onClick={handleClose}
              variant="quiet"
              aria-label="Cerrar"
              className="w-8 h-8 cursor-pointer"
            >
              <Icon type="far" name="times" />
            </Button>
          </div>
        }
        footer={
          <div
            className={`w-full grid py-3 gap-3 px-8 ${
              canDeactivate && canDelete
                ? "grid-cols-1 tablet:grid-cols-[auto_auto_1fr_auto]"
                : "grid-cols-1 tablet:grid-cols-[auto_1fr_auto]"
            }`}
          >
            {canDeactivate && canDelete && (
              <Button
                variant="danger-tertiary"
                size="sm"
                type="button"
                className="w-full md:max-w-[120px] cursor-pointer"
                onClick={() => setShowDeactivateDialog(true)}
              >
                Desactivar
              </Button>
            )}
            <Button
              variant="tertiary"
              size="sm"
              type="button"
              onClick={handleClose}
              className="min-w-[120px] cursor-pointer"
            >
              Cancelar
            </Button>
            <div className="hidden tablet:block" />
            <Button
              variant="default"
              size="sm"
              type="submit"
              disabled={!isValid || isLoadingUpdate}
              form="update-email-template-form"
              className="min-w-[120px] cursor-pointer"
            >
              Guardar cambios
              {isLoadingUpdate && (
                <Icon
                  type="fas"
                  name="spinner-third"
                  className="animate-spin"
                />
              )}
            </Button>
          </div>
        }
      >
        <form
          id="update-email-template-form"
          onSubmit={handleSubmit(onSubmit)}
          className="px-8 space-y-4"
        >
          <div>
            <label
              htmlFor="name"
              className="font-semibold text-body-loud text-sm leading-2xl"
            >
              Nombre de plantilla <span className="text-sm text-danger">*</span>
            </label>
            <input
              id="name"
              type="text"
              maxLength={255}
              placeholder="Escribe el nombre de la plantilla"
              className="w-full border border-neutral-quiet rounded p-2 text-body-loud bg-neutral-object-accent-silent"
              {...register("name")}
            />
            {errors.name?.message && (
              <p className="text-danger text-xs mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="description"
              className="font-semibold text-body-loud text-sm leading-2xl"
            >
              Descripción
            </label>
            <textarea
              id="description"
              maxLength={1000}
              rows={3}
              placeholder="Describe el contenido de la plantilla"
              className="w-full border border-neutral-quiet rounded p-2 text-body-loud bg-neutral-object-accent-silent"
              {...register("description")}
            />
            {errors.description?.message && (
              <p className="text-danger text-xs mt-1">
                {errors.description.message}
              </p>
            )}
          </div>
        </form>
      </ScrollModal>

      <DeactivateEmailTemplateModal
        isOpen={showDeactivateDialog}
        isLoading={isLoadingDelete}
        onClose={() => setShowDeactivateDialog(false)}
        onAccept={handleDeactivate}
      />
    </>
  );
}
